// @ts-nocheck

import { find, some } from 'lodash';
import { createSelector } from 'reselect';

import { DefaultMainBetOutcomes, MainBetOutcomesBySport } from '../../constants/mainbet';
import {
  MINIMUM_BETS_FOR_BET_SLIDER_PRESENTATION,
  MINIMUM_OUTCOMES_FOR_BET_SLIDER_PRESENTATION,
} from '../../constants/presentation';
import { getLang } from '../../i18n/l10n';
import { __ } from '../../i18n/l10n';
import { getBetsDataFromProps, getFilterIdFromProps, getMatchIdFromProps } from '../../selectors/_propsGetters_';
import { selectEntitiesBets } from '../../selectors/entities/bets/entitiesBetsPrimary.selector';
import {
  selectEntitiesMatchesEntity,
  selectEntitiesMatchesEntityAvailable,
  selectEntitiesMatchesEntitySportId,
  selectEntitiesMatchesEntityStatus,
} from '../../selectors/entities/matches/entitiesMatchesPrimary.selector';
import { selectEntitiesOdds } from '../../selectors/entities/odds/entitiesOddsPrimary.selector';
import { selectEntitiesOutcomes } from '../../selectors/entities/outcomes/entitiesOutcomesPrimary.selector';
import { selectEntitiesPlayers } from '../../selectors/entities/players/entitiesPlayersPrimary.selector';
import { selectEntitiesTournaments } from '../../selectors/entities/tournaments/entitiesTournamentsPrimary.selector';
import { makeMatchBetsArraySelector, selectIsMatchMultimatch } from '../../selectors/match.selector';
import { selectRuntimeLicence } from '../../selectors/runtime/runtimePrimary.selector';
import BetGroup from '../../states/betgroup';
import { BetEntity, type BetState } from '../../states/entities/bets';
import { MatchEntity, type MatchState } from '../../states/entities/matches';
import { OutcomeEntity, type OutcomeState } from '../../states/entities/outcomes';
import { type TournamentState } from '../../states/entities/tournaments';
import {
  BetTemplates,
  BetTypes,
  type BetTemplate,
  type SpecialBetGroup,
  SpecialBetGroups,
  type BetType,
  NewBetTypesByLicence,
} from '../../types/bet';
import {
  type BetGroupPlayer,
  PresentationDefaultPositions,
  type PresentationItem,
  PresentationModes,
  type BetGroups,
  type BetGroupEntity,
} from '../../types/betGroup';
import { CustomBetTemplates, type CustomBetTemplate } from '../../types/betTemplate';
import { Locales } from '../../types/common';
import { MarketIds, NewMarketIdsByLicence } from '../../types/market';
import { MatchStatuses } from '../../types/match';
import { getPlayerIdFromFilterId } from '../../types/matchBetFilters';
import { OutcomeModes } from '../../types/outcome';
import { type SelectorMaker, type Selector } from '../../types/selectors';
import { getSrPlayerIdsFromSBV, specifiersToHash } from '../../utils/bet';
import {
  formatBetGroupOutcomes,
  getCustomTemplate,
  getNbColumns,
  getNumberOfLinesFromTemplate,
  getOrderedOutcomes,
  getTemplateHeaders,
} from '../../utils/betTemplate';
import OddUtils, { getScoresColumns } from '../../utils/odd';
import { createResultEqualSelector, createShallowEqualSelector } from '../../utils/reselect';

const SOCCER_GOAL_TYPE_SORTED_CODE = ['penalty', 'free_kick', 'head'];
const SOCCER_COMPETITOR_GOAL_TYPE_SORTED_CODE = ['1', '2', '3', '4', '5'];

const sortSoccerGoalTypeOutcomes = (a: OutcomeState, b: OutcomeState) =>
  SOCCER_GOAL_TYPE_SORTED_CODE.indexOf(a.code) > SOCCER_GOAL_TYPE_SORTED_CODE.indexOf(b.code) ? 1 : -1;

const getLabelForOutcomeCodeCompetitorGoalType = (code: string, match: MatchState): string | null => {
  switch (code) {
    case '1':
      return match.competitor1Name;
    case '2':
      return match.competitor2Name;
    case '3':
      return __('At least one of the two teams');
    case '4':
      return __('Both teams');
    case '5':
      return __('No team');
    default:
      return null;
  }
};

const emptyArray: [] = [];
const emptyObject = {};

// Utilisé dans getSortInfos pour savoir dans quel ordre doivent être triées les outcomes manquants
// en fonction de la colonne
const MissingSortingBehaviours = {
  DEFAULT: 'DEFAULT',
  INVERTED: 'INVERTED',
};
const orderByMissingSortingBehaviour = {
  [MissingSortingBehaviours.DEFAULT]: 100000,
  [MissingSortingBehaviours.INVERTED]: -100000,
};

const marketWithoutSlider = [
  MarketIds.MARKET_ID_SOCCER_NUMBER_OF_SHOTS_BY_PLAYER,
  MarketIds.MARKET_ID_SOCCER_NUMBER_OF_SHOTS_ON_TARGET_BY_PLAYER,
  MarketIds.MARKET_ID_SOCCER_NUMBER_OF_PASSES_BY_PLAYER,
  MarketIds.MARKET_ID_SOCCER_NUMBER_OF_TACKLES_BY_PLAYER,
  MarketIds.MARKET_ID_SOCCER_NUMBER_OF_CARDS_BY_PLAYER,
];

const getSortInfos = (specialBetGroup: SpecialBetGroup) => {
  switch (specialBetGroup) {
    case SpecialBetGroups.SOCCER_SCORERS_PREMATCH:
      return {
        key: 'betType',
        value: BetTypes.SOCCER_ANYTIME_SCORER,
        columns: [BetTypes.SOCCER_ANYTIME_SCORER, BetTypes.SOCCER_FIRST_SCORER, BetTypes.SOCCER_LAST_SCORER],
        missingSortingBehaviours: {
          [BetTypes.SOCCER_ANYTIME_SCORER]: MissingSortingBehaviours.DEFAULT,
          [BetTypes.SOCCER_FIRST_SCORER]: MissingSortingBehaviours.DEFAULT,
          [BetTypes.SOCCER_LAST_SCORER]: MissingSortingBehaviours.DEFAULT,
        },
      };
    case SpecialBetGroups.SOCCER_SCORERS_AND_TEAM_WINS:
    case SpecialBetGroups.SOCCER_NEXT_SCORERS_OVERTIME:
      return {
        key: 'betType',
        value: BetTypes.SOCCER_NEXT_SCORERS_OVERTIME,
        columns: [BetTypes.SOCCER_NEXT_SCORERS_OVERTIME],
        missingSortingBehaviours: {
          [BetTypes.SOCCER_NEXT_SCORERS_OVERTIME]: MissingSortingBehaviours.DEFAULT,
        },
      };
    case SpecialBetGroups.SOCCER_MULTI_SCORERS_PREMATCH:
    case SpecialBetGroups.SOCCER_MULTI_SCORER_1_HT:
    case SpecialBetGroups.SOCCER_MULTI_SCORER_2_HT: {
      const language = getLang();
      if ([Locales.DE, Locales.PT].indexOf(language) !== -1) {
        return {
          key: 'specialBetValue',
          value: 'goals=2',
          columns: ['goals=2', 'goals=3'],
          missingSortingBehaviours: {
            'goals=2': MissingSortingBehaviours.DEFAULT,
            'goals=3': MissingSortingBehaviours.DEFAULT,
          },
        };
      } else {
        return {
          key: 'specialBetValue',
          value: 'goals=2',
          columns: ['goals=2', 'goals=3', 'goals=4'],
          missingSortingBehaviours: {
            'goals=2': MissingSortingBehaviours.DEFAULT,
            'goals=3': MissingSortingBehaviours.DEFAULT,
            'goals=4': MissingSortingBehaviours.DEFAULT,
          },
        };
      }
    }
    case SpecialBetGroups.SOCCER_SCORERS_LIVE:
      return {
        key: 'betType',
        value: BetTypes.SOCCER_NEXT_SCORER_LIVE,
        columns: [BetTypes.SOCCER_ANYTIME_SCORER, BetTypes.SOCCER_NEXT_SCORER_LIVE, BetTypes.SOCCER_LAST_SCORER],
        missingSortingBehaviours: {
          [BetTypes.SOCCER_ANYTIME_SCORER]: MissingSortingBehaviours.DEFAULT,
          [BetTypes.SOCCER_NEXT_SCORER_LIVE]: MissingSortingBehaviours.DEFAULT,
          [BetTypes.SOCCER_LAST_SCORER]: MissingSortingBehaviours.DEFAULT,
        },
      };
    case SpecialBetGroups.SOCCER_DECISIVE_PLAYERS:
      return {
        key: 'marketId',
        value: MarketIds.MARKET_ID_SOCCER_DECISIVE_PLAYER,
        columns: [
          MarketIds.MARKET_ID_SOCCER_DECISIVE_PLAYER,
          MarketIds.MARKET_ID_ANYTIME_GOALSCORER,
          MarketIds.MARKET_ID_SOCCER_PLAYER_PROP_ASSISTS,
        ],
        missingSortingBehaviours: {
          [MarketIds.MARKET_ID_ANYTIME_GOALSCORER]: MissingSortingBehaviours.DEFAULT,
          [MarketIds.MARKET_ID_SOCCER_PLAYER_PROP_ASSISTS]: MissingSortingBehaviours.DEFAULT,
          [MarketIds.MARKET_ID_SOCCER_DECISIVE_PLAYER]: MissingSortingBehaviours.INVERTED,
        },
      };
    case SpecialBetGroups.SOCCER_SCORERS_LIVE_WITHOUT_LAST_SCORER:
      return {
        key: 'betType',
        value: BetTypes.SOCCER_NEXT_SCORER_LIVE,
        columns: [BetTypes.SOCCER_ANYTIME_SCORER, BetTypes.SOCCER_NEXT_SCORER_LIVE],
        missingSortingBehaviours: {
          [BetTypes.SOCCER_ANYTIME_SCORER]: MissingSortingBehaviours.DEFAULT,
          [BetTypes.SOCCER_NEXT_SCORER_LIVE]: MissingSortingBehaviours.DEFAULT,
        },
      };
    case SpecialBetGroups.SOCCER_MULTI_SCORERS_LIVE:
      return {
        key: 'betType',
        value: BetTypes.SOCCER_MULTI_SCORERS_2,
        columns: [BetTypes.SOCCER_MULTI_SCORERS_2, BetTypes.SOCCER_MULTI_SCORERS_3],
        missingSortingBehaviours: {
          [BetTypes.SOCCER_MULTI_SCORERS_2]: MissingSortingBehaviours.DEFAULT,
          [BetTypes.SOCCER_MULTI_SCORERS_3]: MissingSortingBehaviours.INVERTED,
        },
      };
    case SpecialBetGroups.SOCCER_SCORER_PERIOD_HALF_TIME:
      return {
        key: 'betType',
        value: BetTypes.SOCCER_SCORER_HALF_TIME_FIRST,
        columns: [
          BetTypes.SOCCER_SCORER_HALF_TIME_FIRST,
          BetTypes.SOCCER_SCORER_HALF_TIME_SECOND,
          BetTypes.SOCCER_SCORER_HALF_TIME_BOTH,
        ],
        missingSortingBehaviours: {
          [BetTypes.SOCCER_SCORER_HALF_TIME_FIRST]: MissingSortingBehaviours.DEFAULT,
          [BetTypes.SOCCER_SCORER_HALF_TIME_SECOND]: MissingSortingBehaviours.DEFAULT,
          [BetTypes.SOCCER_SCORER_HALF_TIME_BOTH]: MissingSortingBehaviours.DEFAULT,
        },
      };
    case SpecialBetGroups.SOCCER_SCORER_PERIOD_10MINUTES:
      return {
        key: 'betType',
        value: BetTypes.SOCCER_SCORER_PERIOD_10MINUTES,
        columns: [BetTypes.SOCCER_SCORER_PERIOD_10MINUTES],
        missingSortingBehaviours: {
          [BetTypes.SOCCER_SCORER_PERIOD_10MINUTES]: MissingSortingBehaviours.DEFAULT,
        },
      };

    case SpecialBetGroups.RUGBY_7S_SCORERS:
    case SpecialBetGroups.RUGBY_LEAGUE_SCORERS:
    case SpecialBetGroups.RUGBY_SCORERS:
    case SpecialBetGroups.MULTICHOICE_MM_RUGBY_SCORERS:
    case SpecialBetGroups.MULTICHOICE_MM_RUGBY_LEAGUE_SCORERS:
    case SpecialBetGroups.FOOTBALL_SCORERS:
    case SpecialBetGroups.MULTICHOICE_MM_FOOTBALL_SCORERS:
      return {
        key: 'specialBetValue',
        value: 'type=prematch',
        columns: ['type=prematch', 'goals=2', 'goals=3'],
        missingSortingBehaviours: {
          'type=prematch': MissingSortingBehaviours.DEFAULT,
          'goals=2': MissingSortingBehaviours.DEFAULT,
          'goals=3': MissingSortingBehaviours.DEFAULT,
        },
      };
    case SpecialBetGroups.RUGBY_7S_SCORERS_LIVE:
    case SpecialBetGroups.RUGBY_LEAGUE_SCORERS_LIVE:
    case SpecialBetGroups.RUGBY_SCORERS_LIVE:
      return {
        key: 'specialBetValue',
        value: 'type=live',
        columns: ['type=live', 'goals=2', 'goals=3'],
        missingSortingBehaviours: {
          'type=live': MissingSortingBehaviours.DEFAULT,
          'goals=2': MissingSortingBehaviours.DEFAULT,
          'goals=3': MissingSortingBehaviours.DEFAULT,
        },
      };
    case SpecialBetGroups.ICE_HOCKEY_SCORERS:
    case SpecialBetGroups.MULTICHOICE_MM_ICE_HOCKEY_SCORERS:
      return {
        key: 'specialBetValue',
        value: undefined,
        columns: [undefined, 'goals=2', 'goals=3'],
        missingSortingBehaviours: {
          undefined: MissingSortingBehaviours.DEFAULT,
          'goals=2': MissingSortingBehaviours.DEFAULT,
          'goals=3': MissingSortingBehaviours.DEFAULT,
        },
      };
    case SpecialBetGroups.ICE_HOCKEY_SCORER_PERIOD:
      return {
        key: 'betType',
        value: BetTypes.ICE_HOCKEY_SCORER_FIRST_PERIOD,
        columns: [
          BetTypes.ICE_HOCKEY_SCORER_FIRST_PERIOD,
          BetTypes.ICE_HOCKEY_SCORER_SECOND_PERIOD,
          BetTypes.ICE_HOCKEY_SCORER_THIRD_PERIOD,
        ],
        missingSortingBehaviours: {
          [BetTypes.ICE_HOCKEY_SCORER_FIRST_PERIOD]: MissingSortingBehaviours.DEFAULT,
          [BetTypes.ICE_HOCKEY_SCORER_SECOND_PERIOD]: MissingSortingBehaviours.DEFAULT,
          [BetTypes.ICE_HOCKEY_SCORER_THIRD_PERIOD]: MissingSortingBehaviours.DEFAULT,
        },
      };
    case SpecialBetGroups.ICE_HOCKEY_SCORER_LIVE:
      return {
        key: 'betType',
        value: BetTypes.ICE_HOCKEY_NEXT_SCORER,
        columns: [BetTypes.ICE_HOCKEY_SIMPLE_SCORER, BetTypes.ICE_HOCKEY_NEXT_SCORER],
        missingSortingBehaviours: {
          [BetTypes.ICE_HOCKEY_SIMPLE_SCORER]: MissingSortingBehaviours.DEFAULT,
          [BetTypes.ICE_HOCKEY_NEXT_SCORER]: MissingSortingBehaviours.INVERTED,
        },
      };
    case SpecialBetGroups.ICE_HOCKEY_MULTI_SCORER_LIVE:
      return {
        key: 'specialBetValue',
        value: 'goals=2',
        columns: ['goals=2', 'goals=3'],
        missingSortingBehaviours: {
          'goals=2': MissingSortingBehaviours.DEFAULT,
          'goals=3': MissingSortingBehaviours.INVERTED,
        },
      };
    default:
      return {
        key: null,
        value: '',
        columns: emptyArray,
        missingSortingBehaviours: {},
      };
  }
};

const getExpandPreviewLines = (nbLines, firstBetTemplate) => {
  if (firstBetTemplate) {
    const { lineThreshold, previewLines } = getNumberOfLinesFromTemplate(firstBetTemplate);
    // if the real number of lines if strictly superior to the threshold then we display the expand button
    // ... otherwise we hide it.
    const shouldShowExpand = nbLines > lineThreshold;
    if (shouldShowExpand) {
      return previewLines;
    }
  }
  return undefined;
};

const haveOutcomeWithoutSpecifier = (groups, specifier) => {
  return groups?.some((group) => {
    return group.items.some((item) =>
      item?.outcomes.some((outcome) => {
        const specifiers = specifiersToHash(outcome.specialBetValue);

        return !specifiers?.[specifier];
      }),
    );
  });
};

const havePlayerAtLeast3Lines = (groups) =>
  groups?.reduce((acc, group) => {
    if (group?.items.find((item) => item?.outcomes?.length >= MINIMUM_OUTCOMES_FOR_BET_SLIDER_PRESENTATION)) {
      return acc + 1;
    }
    return acc;
  }, 0) > 0;

const sortSoccerPlayersGoalType = (a, b) => {
  // Necessité de remapper les outcomes aux colonnes pour récupérer l'index correspondant
  const aPenalty = find(a, (v) => v.code === 'penalty')?.value || 0;
  const aFreekick = find(a, (v) => v.code === 'free_kick')?.value || 0;
  const aHead = find(a, (v) => v.code === 'head')?.value || 0;
  const bPenalty = find(b, (v) => v.code === 'penalty')?.value || 0;
  const bFreekick = find(b, (v) => v.code === 'free_kick')?.value || 0;
  const bHead = find(b, (v) => v.code === 'head')?.value || 0;

  // Pondération pour classer par pénalty > tete > coup franc
  const aValue = aPenalty * 1000000 + aFreekick * 10000 + aHead * 100;
  const bValue = bPenalty * 1000000 + bFreekick * 10000 + bHead * 100;

  return aValue - bValue;
};

const getColumnItems = (betType, outcomes, matchId, betId, marketId, specialBetValue) => {
  const sortOutcomes = getScoresColumns(betType)(outcomes);
  return ['left', 'center', 'right'].reduce((acc, column) => {
    const columnOutcomes = sortOutcomes[column];
    if (columnOutcomes.length) {
      acc.push(formatBetGroupOutcomes(columnOutcomes, matchId, betId, marketId, specialBetValue));
    }
    return acc;
  }, []);
};

type BetGroupFactoryPropsType = {
  groups: BetGroups;
  headers: Array<string>;
  betTemplate: (CustomBetTemplate | BetTemplate) | null;
  matchIsAvailable: boolean;
  showPercentDistribution: boolean;
  showStarterFilter: boolean;
  betPresentationModes: Array<PresentationItem>;
  betTypeId: BetType | null;
  marketId?: number;
  isAlternativeMainBet: boolean;
  betTitle?: string;
  betId?: number;
  matchCompetitorsNbStarters: Array<number>;
  isMultichoice: boolean;
  isMultimatch: boolean;
};

const makeBetGroupFactoryPropsSelector: SelectorMaker<BetGroupFactoryPropsType> = () => {
  const selectEntitiesMatchesEntityBets = makeMatchBetsArraySelector();

  const selectPlayerIdFromFilterIdFromPropsOfPlayerKind: Selector<number | null> = createSelector(
    getFilterIdFromProps,
    (filterId) => getPlayerIdFromFilterId(filterId),
  );

  const selectSpecificBets: Selector<Array<BetState>> = createSelector(
    [getBetsDataFromProps, selectEntitiesBets],
    (betsData, bets) =>
      betsData?.reduce((acc, betData) => {
        const bet = bets[betData.betId];
        if (bet) {
          return [...acc, bet];
        }
        return acc;
      }, []) ?? emptyArray,
  );

  const selectFirstBetData: Selector<
    | {
        betId: number;
        isMultichoice: boolean;
      }
    | undefined
  > = createSelector(getBetsDataFromProps, (betsData) => betsData?.[0]);

  const selectFirstBetId: Selector<number | undefined> = createSelector(
    [selectFirstBetData],
    (firstBetData) => firstBetData?.betId,
  );

  const selectFirstBet: Selector<BetState | undefined> = createSelector(
    [selectFirstBetId, selectEntitiesBets],
    (firstBetId, bets) => bets[firstBetId],
  );

  const selectBetCurrentTotal: Selector<number | undefined> = createSelector(
    [selectFirstBet],
    (firstBet) => firstBet?.currentTotal,
  );

  const selectBetTitle: Selector<string | undefined> = createSelector(
    [selectFirstBet],
    (firstBet) => firstBet?.betTitle,
  );

  const selectBetIsMultichoice: Selector<boolean> = createSelector(
    selectFirstBetData,
    (firstBetData) => firstBetData?.isMultichoice ?? false,
  );

  const selectBetIsAvailable: Selector<boolean> = createSelector(
    [selectFirstBet],
    (firstBet) => firstBet?.available || false,
  );

  const selectBetTypeId: Selector<BetType | null> = createSelector(
    [selectFirstBet],
    (firstBet) => firstBet?.betType || null,
  );

  const selectBetId: Selector<number | undefined> = createSelector([selectFirstBet], (firstBet) => firstBet?.betId);

  const selectMarketId: Selector<number | undefined> = createSelector(
    [selectFirstBet],
    (firstBet) => firstBet?.marketId,
  );

  const selectIsAlternativeMainBet: Selector<boolean> = createSelector(
    [selectFirstBet],
    (firstBet) => firstBet?.isAlternativeMainBet ?? false,
  );

  const selectFirstBetTemplate: Selector<BetTemplate | null | undefined> = createSelector(
    [selectFirstBet],
    (firstBet) => firstBet?.template ?? null,
  );

  const selectSpecialBetGroup: Selector<SpecialBetGroup> = createSelector(
    [selectEntitiesMatchesEntity, selectFirstBet, makeMatchBetsArraySelector(), selectBetIsMultichoice],
    (match, firstBet, matchBets, isFromMultichoice) =>
      BetGroup.getSpecial(match, firstBet, matchBets, isFromMultichoice),
  );

  const selectCustomTemplate: Selector<CustomBetTemplate | null> = createSelector(
    [selectEntitiesMatchesEntity, selectFirstBet, selectSpecialBetGroup],
    (match, firstBet, specialBetGroup) => (firstBet ? getCustomTemplate(specialBetGroup, match, firstBet) : null),
  );

  const selectBetTemplate: Selector<CustomBetTemplate | BetTemplate | null | undefined> = createSelector(
    [selectCustomTemplate, selectFirstBetTemplate],
    (customTemplate, firstBetTemplate) => customTemplate || firstBetTemplate,
  );

  const selectSpecificBetOutcomesWithValue: Selector<{
    [betIndex: number]: OutcomeState & { value: number };
  }> = createShallowEqualSelector(
    [selectSpecificBets, selectEntitiesOdds, selectEntitiesOutcomes],
    (bets, odds, outcomes) => {
      return (
        bets?.reduce((outcomesWithValues, bet) => {
          const betOutcomes = bet?.outcomes?.map((outcomeId) => ({
            ...outcomes[outcomeId],
            value: odds[outcomeId],
          }));
          if (betOutcomes?.length) {
            return {
              ...outcomesWithValues,
              [bet.betId]: betOutcomes,
            };
          }
          return outcomesWithValues;
        }, {}) ?? emptyObject
      );
    },
  );

  const selectBetOutcomesWithValue: Selector<{
    [betIndex: number]: OutcomeState & { value: number };
  }> = createResultEqualSelector([selectSpecificBetOutcomesWithValue], (result) => {
    return result;
  });

  const selectGroupOverUnder: Selector<BetGroups | null> = createShallowEqualSelector(
    [selectSpecificBets, selectBetOutcomesWithValue, selectFirstBet, getMatchIdFromProps, selectBetTemplate],
    (bets, betOutcomesWithValue, firstBet, matchId, betTemplate) => {
      const isMatching = betTemplate === BetTemplates.OVER_UNDER || betTemplate === CustomBetTemplates.SOCCER_VERSUS;

      if (isMatching) {
        const template: unknown = firstBet?.template ? String(firstBet.template) : null;

        const items = bets.map<BetGroupPlayer>((bet) => {
          const { betId, marketId, specialBetValue } = bet;
          const betOutcomes = betOutcomesWithValue[betId];
          const orderedOutcomes = template && betOutcomes ? getOrderedOutcomes(betOutcomes, template) : betOutcomes;
          const outcomes = formatBetGroupOutcomes(orderedOutcomes, matchId, betId, marketId, specialBetValue);
          const showPercentDistribution = some(orderedOutcomes, (outcome) => !!outcome.percentDistribution);
          return {
            label: null,
            outcomes,
            showPercentDistribution,
          };
        });

        return [
          {
            items,
            nbLines: items.length,
            expandPreviewLines: getExpandPreviewLines(items.length, firstBet?.template),
          },
        ];
      }

      return null;
    },
  );

  const selectGroupHeadToHead: Selector<BetGroups | null> = createShallowEqualSelector(
    [selectSpecificBets, selectBetOutcomesWithValue, selectFirstBet, getMatchIdFromProps, selectBetTemplate],
    (bets, betOutcomesWithValue, firstBet, matchId, betTemplate) => {
      const isMatching = betTemplate === CustomBetTemplates.BASKETBALL_HEAD_TO_HEAD;

      if (isMatching) {
        const template: unknown = firstBet?.template ? String(firstBet.template) : null;

        const items = bets.reduce((acc, bet) => {
          const { betId, marketId, specialBetValue } = bet;

          const betOutcomes = betOutcomesWithValue[betId];

          const orderedOutcomes = template && betOutcomes ? getOrderedOutcomes(betOutcomes, template) : betOutcomes;
          const outcomes = formatBetGroupOutcomes(orderedOutcomes, matchId, betId, marketId, specialBetValue);

          return [
            ...acc,
            ...outcomes.map(({ id }) => ({
              betId,
              id,
              marketId,
              matchId,
              specialBetValue,
            })),
          ];
        }, []);

        return [
          {
            items,
            nbLines: items.length,
            expandPreviewLines: getExpandPreviewLines(items.length, betTemplate),
          },
        ];
      }

      return null;
    },
  );

  const selectGroupAsians: Selector<BetGroups | null> = createShallowEqualSelector(
    [selectSpecificBets, selectBetOutcomesWithValue, selectFirstBet, getMatchIdFromProps, selectBetTemplate],
    (bets, betOutcomesWithValue, firstBet, matchId, betTemplate) => {
      const isMatching =
        betTemplate === BetTemplates.ASIAN_HANDICAP || betTemplate === BetTemplates.ASIAN_TEXT_HANDICAP;

      if (isMatching) {
        const template: unknown = firstBet?.template ?? null;

        const items = bets.map<BetGroupPlayer>((bet) => {
          const { betId, betTitle, marketId, specialBetValue } = bet;
          const betOutcomes = betOutcomesWithValue[betId];
          const orderedOutcomes = template && betOutcomes ? getOrderedOutcomes(betOutcomes, template) : betOutcomes;

          let label = null;
          if (template === BetTemplates.ASIAN_TEXT_HANDICAP) {
            label = betTitle.split('|')[1];
          }

          const outcomes = formatBetGroupOutcomes(orderedOutcomes, matchId, betId, marketId, specialBetValue);

          // at least one outcome has a percent distribution
          const showPercentDistribution = some(orderedOutcomes, (outcome) => !!outcome.percentDistribution);
          return {
            label,
            outcomes,
            showPercentDistribution,
          };
        });

        return [
          {
            items,
            nbLines: items.length,
            expandPreviewLines: getExpandPreviewLines(items.length, firstBet?.template),
          },
        ];
      }

      return null;
    },
  );

  const selectGroupScorerGoalType: Selector<BetGroups | null> = createShallowEqualSelector(
    [
      selectSpecificBets,
      selectBetOutcomesWithValue,
      getMatchIdFromProps,
      selectBetTemplate,
      selectFirstBetTemplate,
      selectEntitiesPlayers,
    ],
    (bets, betOutcomesWithValue, matchId, betTemplate, firstBetTemplate, playerEntity) => {
      const isMatching = betTemplate === CustomBetTemplates.SOCCER_GOAL_TYPE;

      if (isMatching) {
        const players = bets.map(({ betId, specialBetValue, marketId }) => {
          const label = (specialBetValue || '').replace('player=', '');
          const outcomes = betOutcomesWithValue[betId];
          return {
            label,
            outcomes,
            betId,
            marketId,
            specialBetValue,
          };
        });

        players.sort((playerA, playerB) => sortSoccerPlayersGoalType(playerA.outcomes, playerB.outcomes));
        const items = players.map<BetGroupPlayer>(({ outcomes, label, betId, marketId, specialBetValue }) => {
          const showPercentDistribution = some(outcomes, (outcome) => !!outcome.percentDistribution);
          const filledOutcomes = SOCCER_GOAL_TYPE_SORTED_CODE.map((code) => {
            return outcomes.filter((outcome) => outcome.code === code)[0] ?? { id: null, code: code };
          });
          const sortOutcomes = filledOutcomes.sort(sortSoccerGoalTypeOutcomes);
          const formatOutcomes = formatBetGroupOutcomes(sortOutcomes, matchId, betId, marketId, specialBetValue);

          return {
            label,
            starter: sortOutcomes.some((o) => playerEntity[o.playerId]?.starter),
            outcomes: formatOutcomes,
            showPercentDistribution,
          };
        });

        return [
          {
            items,
            nbLines: items.length,
            expandPreviewLines: getExpandPreviewLines(items.length, firstBetTemplate),
          },
        ];
      }

      return null;
    },
  );

  const selectGroupCompetitorGoalType: Selector<BetGroups | null> = createShallowEqualSelector(
    [
      selectSpecificBets,
      selectBetOutcomesWithValue,
      getMatchIdFromProps,
      selectBetTemplate,
      selectFirstBetTemplate,
      selectEntitiesMatchesEntity,
    ],
    (bets, betOutcomesWithValue, matchId, betTemplate, firstBetTemplate, match) => {
      const isMatching = betTemplate === CustomBetTemplates.SOCCER_COMPETITOR_GOAL_TYPE;

      if (isMatching) {
        const marketOrder = [
          MarketIds.MARKET_ID_SOCCER_COMPETITOR_GOAL_TYPE_PENALTY,
          MarketIds.MARKET_ID_SOCCER_COMPETITOR_GOAL_TYPE_FREEKICK,
          MarketIds.MARKET_ID_SOCCER_COMPETITOR_GOAL_TYPE_HEAD,
        ];
        const orderedBets = bets.sort((a, b) => {
          const mA = a.marketId;
          const mB = b.marketId;

          return marketOrder.indexOf(mA) - marketOrder.indexOf(mB);
        });
        const betList = orderedBets.map(({ betId, betTitle, marketId, specialBetValue }) => {
          const label = betTitle;
          const outcomes = betOutcomesWithValue[betId];
          return {
            label,
            outcomes,
            betId,
            marketId,
            specialBetValue,
          };
        });

        const items = [];
        SOCCER_COMPETITOR_GOAL_TYPE_SORTED_CODE.forEach((code) => {
          const outcomes = betList.map((bet) => {
            const outcome = bet.outcomes.filter((outcome) => outcome.code === code)[0];

            if (outcome) {
              return {
                matchId,
                betId: bet.betId,
                id: outcome.outcomeId,
                marketId: bet.marketId,
                specialBetValue: bet.specialBetValue,
              };
            }
            return null;
          });

          if (outcomes?.length) {
            items.push({
              label: getLabelForOutcomeCodeCompetitorGoalType(code, match),
              outcomes,
              showPercentDistribution: false,
            });
          }
        });

        return [
          {
            items,
            nbLines: items.length,
            expandPreviewLines: getExpandPreviewLines(items.length, firstBetTemplate),
          },
        ];
      }

      return null;
    },
  );

  const selectGroupDecisivePlayers = createShallowEqualSelector(
    [
      selectEntitiesPlayers,
      selectBetOutcomesWithValue,
      selectFirstBet,
      selectEntitiesMatchesEntity,
      selectFirstBetTemplate,
      selectEntitiesMatchesEntityAvailable,
      selectBetIsAvailable,
      selectSpecialBetGroup,
      getMatchIdFromProps,
      selectBetTemplate,
      selectPlayerIdFromFilterIdFromPropsOfPlayerKind,
      selectEntitiesMatchesEntityBets,
      selectEntitiesOutcomes,
      selectEntitiesOdds,
    ],
    (
      playersEntity,
      betOutcomesWithValue,
      firstBet,
      match,
      template,
      matchIsAvailable,
      betIsAvailable,
      specialBetGroup,
      matchId,
      betTemplate,
      playerIdFromFilterId,
      matchBets,
      outcomesMap,
      oddsMap,
    ) => {
      const matchingTemplates = [CustomBetTemplates.SOCCER_DECISIVE_PLAYERS];

      const isMatching = !!~matchingTemplates.indexOf(betTemplate);

      if (isMatching) {
        const playerFromFilterId = playersEntity[playerIdFromFilterId];
        const srPlayerIdFromFilterId = playerFromFilterId?.srPlayerId;

        const {
          columns: sortColumns,
          key,
          value: sortValue,
          missingSortingBehaviours,
        }: unknown = getSortInfos(specialBetGroup);

        const betsByColumn = sortColumns.map((columnId) => {
          if (columnId === MarketIds.MARKET_ID_SOCCER_DECISIVE_PLAYER) {
            return firstBet;
          }

          if (columnId === MarketIds.MARKET_ID_SOCCER_PLAYER_PROP_ASSISTS) {
            const playerOutcomes = matchBets.reduce((acc, bet) => {
              const { marketId, betId, outcomes: outcomeIds } = bet;

              if (marketId === MarketIds.MARKET_ID_SOCCER_PLAYER_PROP_ASSISTS) {
                for (const outcomeId of outcomeIds) {
                  const outcome = outcomesMap[outcomeId];

                  if (outcome) {
                    const { code } = outcome;
                    const splitted = code.split(':');

                    if (splitted.length !== 1 && splitted[splitted.length - 1] === '1') {
                      return [
                        ...acc,
                        {
                          betId,
                          outcomeId: outcome.outcomeId,
                        },
                      ];
                    }
                  }
                }
              }

              return acc;
            }, []);

            return {
              marketId: columnId,
              outcomes: playerOutcomes,
            };
          }

          return matchBets.find((bet) => bet[key] === columnId);
        });

        let showPercentDistribution = false;

        const players = betsByColumn.reduce((acc, bet) => {
          const { outcomes, betId, marketId, specialBetValue } = bet;
          const outcomesWithValue = outcomes.reduce((_acc, outcomeData) => {
            const nextOutcome = {
              ...outcomesMap[typeof outcomeData === 'number' ? outcomeData : outcomeData.outcomeId],
              value: oddsMap[typeof outcomeData === 'number' ? outcomeData : outcomeData.outcomeId],
            };

            if (typeof outcomeData === 'object') {
              const { betId: outcomeBetId } = outcomeData;
              nextOutcome.betId = outcomeBetId;
            }

            return [..._acc, nextOutcome];
          }, []);

          const column = bet[key];

          for (const outcomeData of outcomes) {
            const outcomeId = typeof outcomeData === 'number' ? outcomeData : outcomeData.outcomeId;
            const outcome = outcomesWithValue.find((o) => o.outcomeId === outcomeId) ?? {};
            const { playerId, value, label, available, team, code, flag, betId: outcomeBetId } = outcome;
            const player = playersEntity[playerId];
            const srPlayerIdsFromCode = getSrPlayerIdsFromSBV(code);

            // at least one outcome has a percent distribution
            if (!showPercentDistribution) {
              showPercentDistribution = !!outcome.percentDistribution;
            }

            if (
              (playerId && (!playerIdFromFilterId || (playerIdFromFilterId && playerId === playerIdFromFilterId))) ||
              (Array.isArray(srPlayerIdsFromCode) && !!~srPlayerIdsFromCode.indexOf(srPlayerIdFromFilterId))
            ) {
              if (!acc[playerId]) {
                acc[playerId] = {
                  label,
                  team,
                  starter: player?.starter,
                  outcomes: {},
                  value,
                  flag,
                };
              }
              const mode = OddUtils.getMode({
                matchIsAvailable,
                betIsAvailable,
                template,
                oddValue: value,
                outcomeIsAvailable: available,
              });

              if (mode !== OutcomeModes.STOP) {
                acc[playerId].outcomes = {
                  ...acc[playerId].outcomes,
                  [column]: {
                    matchId,
                    betId: outcomeBetId ?? betId,
                    marketId,
                    specialBetValue,
                    id: outcomeId,
                    order: 100000 - value,
                  },
                };
              }
            }
          }

          return acc;
        }, {});

        const playerIds = Object.keys(players);

        for (const playerId of playerIds) {
          const player = players[playerId];
          const playerOutcomeKeys = Object.keys(player.outcomes);

          if (playerOutcomeKeys.length !== sortColumns.length) {
            sortColumns.forEach((column) => {
              if (!player.outcomes[column]) {
                const missingSortingBehaviour = missingSortingBehaviours[column];
                const missingSortingOrder = orderByMissingSortingBehaviour[missingSortingBehaviour];

                player.outcomes[column] = {
                  matchId,
                  betId: null,
                  id: null,
                  order: missingSortingOrder,
                  marketId: null,
                  specialBetValue: null,
                };
              }
            });
          }
        }

        const playerArr = playerIds.map((playerId) => players[playerId]);
        const orderSortColumns = [sortValue, ...sortColumns.filter((col) => col !== sortValue)];

        const sorted = playerArr.sort((a, b) => {
          for (let i = 0; i < orderSortColumns.length; i++) {
            const orderA = a?.outcomes[orderSortColumns[i]]?.order ?? 100000;
            const orderB = b?.outcomes[orderSortColumns[i]]?.order ?? 100000;

            if (orderA - orderB !== 0) {
              return -Math.sign(orderA - orderB);
            }
          }
          return 0;
        });

        const finalPlayers = sorted.reduce((acc, item) => {
          const { label, team, starter, flag, outcomes } = item;

          if (outcomes) {
            const keys = Object.keys(outcomes);

            if (
              keys.length === sortColumns.length &&
              keys.filter((col) => !!outcomes[col]?.id).length === sortColumns.length
            ) {
              return [
                ...acc,
                {
                  label,
                  team,
                  flag,
                  starter,
                  outcomes: sortColumns.map((columnId) => outcomes[columnId]),
                },
              ];
            }
          }

          return acc;
        }, []);

        const groups = finalPlayers.reduce((acc, item) => {
          if (!acc[item.team]) {
            acc[item.team] = {
              items: [],
              label: MatchEntity.getCompetitorName(match, item.team),
              teamId: item.team,
              flag: item.flag,
            };
          }

          acc[item.team].items.push(item);

          return acc;
        }, {});

        const groupsIds = Object.keys(groups);
        const groupsArr = groupsIds.map((groupId) => groups[groupId]);

        return groupsArr.map(({ items, label, teamId, flag }) => ({
          groupLabel: label,
          teamId,
          flag: flag,
          items,
          showPercentDistribution,
          nbLines: items?.length || 0,
          expandPreviewLines: getExpandPreviewLines(items.length, betTemplate),
        }));
      }

      return null;
    },
  );

  const selectGroupScorers: Selector<BetGroups | null> = createShallowEqualSelector(
    [
      selectSpecificBets,
      selectEntitiesPlayers,
      selectBetOutcomesWithValue,
      selectFirstBet,
      selectEntitiesMatchesEntity,
      selectFirstBetTemplate,
      selectEntitiesMatchesEntityAvailable,
      selectBetIsAvailable,
      selectSpecialBetGroup,
      getMatchIdFromProps,
      selectBetTemplate,
      selectPlayerIdFromFilterIdFromPropsOfPlayerKind,
    ],
    (
      bets,
      playersEntity,
      betOutcomesWithValue,
      firstBet,
      match,
      template,
      matchIsAvailable,
      betIsAvailable,
      specialBetGroup,
      matchId,
      betTemplate,
      playerIdFromFilterId,
    ) => {
      const matchingTemplates = [
        CustomBetTemplates.SOCCER_SCORERS,
        CustomBetTemplates.FOOTBALL_SCORERS,
        CustomBetTemplates.ICE_HOCKEY_SCORERS,
        CustomBetTemplates.RUGBY_SCORERS,
      ];

      const isMatching = !!~matchingTemplates.indexOf(betTemplate);

      if (isMatching) {
        const playerFromFilterId = playersEntity[playerIdFromFilterId];
        const srPlayerIdFromFilterId = playerFromFilterId?.srPlayerId;

        let showPercentDistribution = false;

        const {
          columns: sortColumns,
          key,
          value: sortValue,
          missingSortingBehaviours,
        }: unknown = getSortInfos(specialBetGroup);

        const players = bets.reduce((acc, bet) => {
          const { outcomes: outcomeIds, betId, marketId, specialBetValue } = bet;
          const outcomesWithValue = betOutcomesWithValue[betId];
          const column = bet[key];

          for (const outcomeId of outcomeIds) {
            const outcome = outcomesWithValue.find((o) => o.outcomeId === outcomeId) ?? {};
            const { playerId, value, label, available, team, code, flag } = outcome;
            const player = playersEntity[playerId];
            const srPlayerIdsFromCode = getSrPlayerIdsFromSBV(code);

            // at least one outcome has a percent distribution
            if (!showPercentDistribution) {
              showPercentDistribution = !!outcome.percentDistribution;
            }

            if (
              (playerId && (!playerIdFromFilterId || (playerIdFromFilterId && playerId === playerIdFromFilterId))) ||
              (Array.isArray(srPlayerIdsFromCode) && !!~srPlayerIdsFromCode.indexOf(srPlayerIdFromFilterId))
            ) {
              if (!acc[playerId]) {
                acc[playerId] = {
                  label,
                  team,
                  starter: player?.starter,
                  outcomes: {},
                  value,
                  flag,
                };
              }
              const mode = OddUtils.getMode({
                matchIsAvailable,
                betIsAvailable,
                template,
                oddValue: value,
                outcomeIsAvailable: available,
              });

              if (mode !== OutcomeModes.STOP) {
                acc[playerId].outcomes = {
                  ...acc[playerId].outcomes,
                  [column]: {
                    matchId,
                    betId,
                    marketId,
                    specialBetValue,
                    id: outcomeId,
                    order: 100000 - value,
                  },
                };
              }
            }
          }

          return acc;
        }, {});

        const playerIds = Object.keys(players);

        for (const playerId of playerIds) {
          const player = players[playerId];
          const playerOutcomeKeys = Object.keys(player.outcomes);

          if (playerOutcomeKeys.length !== sortColumns.length) {
            sortColumns.forEach((column) => {
              if (!player.outcomes[column]) {
                const missingSortingBehaviour = missingSortingBehaviours[column];
                const missingSortingOrder = orderByMissingSortingBehaviour[missingSortingBehaviour];

                player.outcomes[column] = {
                  matchId,
                  betId: null,
                  id: null,
                  order: missingSortingOrder,
                  marketId: null,
                  specialBetValue: null,
                };
              }
            });
          }
        }

        const playerArr = playerIds.map((playerId) => players[playerId]);
        const orderSortColumns = [sortValue, ...sortColumns.filter((col) => col !== sortValue)];

        const sorted = playerArr.sort((a, b) => {
          for (let i = 0; i < orderSortColumns.length; i++) {
            const orderA = a?.outcomes[orderSortColumns[i]]?.order ?? 0;
            const orderB = b?.outcomes[orderSortColumns[i]]?.order ?? 0;

            if (orderA - orderB !== 0) {
              return -Math.sign(orderA - orderB);
            }
          }
          return 0;
        });

        const finalPlayers = sorted
          .filter((item) => Object.keys(item.outcomes).length > 0)
          .map(({ label, team, starter, outcomes, flag }) => {
            let finalOutcomes = [];
            if (sortColumns) {
              finalOutcomes = sortColumns.map((columnId) => outcomes[columnId]);
            }

            return {
              label,
              team,
              flag,
              starter,
              outcomes: finalOutcomes,
            };
          });

        const groups = finalPlayers.reduce((acc, item) => {
          if (!acc[item.team]) {
            acc[item.team] = {
              items: [],
              label: MatchEntity.getCompetitorName(match, item.team),
              teamId: item.team,
              flag: item.flag,
            };
          }
          acc[item.team].items.push(item);
          return acc;
        }, {});

        const groupsIds = Object.keys(groups);
        const groupsArr = groupsIds.map((groupId) => groups[groupId]);

        return groupsArr.map(({ items, label, teamId, flag }) => ({
          groupLabel: label,
          teamId,
          flag: flag || MatchEntity.getCompetitorFlag(match, teamId),
          items: items,
          showPercentDistribution,
          nbLines: items?.length || 0,
          expandPreviewLines: getExpandPreviewLines(items.length, betTemplate),
        }));
      }

      return null;
    },
  );

  const selectScorerHandball: Selector<BetGroups | null> = createShallowEqualSelector(
    [
      selectSpecificBets,
      selectEntitiesPlayers,
      selectBetOutcomesWithValue,
      selectFirstBet,
      selectEntitiesMatchesEntity,
      selectFirstBetTemplate,
      selectEntitiesMatchesEntityAvailable,
      selectBetIsAvailable,
      selectSpecialBetGroup,
      getMatchIdFromProps,
      selectBetTemplate,
      selectPlayerIdFromFilterIdFromPropsOfPlayerKind,
    ],
    (
      bets,
      playersEntity,
      betOutcomesWithValue,
      firstBet,
      match,
      template,
      matchIsAvailable,
      betIsAvailable,
      specialBetGroup,
      matchId,
      betTemplate,
      playerIdFromFilterId,
    ) => {
      const matchingTemplates = [CustomBetTemplates.HANDBALL_SCORERS_PLAYER];

      const isMatching = !!~matchingTemplates.indexOf(betTemplate);

      if (isMatching) {
        const players = bets.reduce((acc, bet, index) => {
          const { outcomes: outcomeIds, betId, marketId, specialBetValue } = bet;

          const outcomesWithValue = betOutcomesWithValue[betId];

          for (const outcomeId of outcomeIds) {
            const outcome = outcomesWithValue.find((o) => o.outcomeId === outcomeId) ?? {};
            const { playerId, value, label, available, team } = outcome;
            const player = playersEntity[playerId];

            if (playerId && (!playerIdFromFilterId || (playerIdFromFilterId && playerId === playerIdFromFilterId))) {
              if (!acc[playerId]) {
                acc[playerId] = {
                  label,
                  outcomes: [],
                  team,
                  srPlayerId: player.srPlayerId,
                  order: index,
                  marketId,
                  specialBetValue,
                };
              }
              const mode = OddUtils.getMode({
                matchIsAvailable,
                betIsAvailable,
                template,
                oddValue: value,
                outcomeIsAvailable: available,
              });

              if (mode !== OutcomeModes.STOP) {
                acc[playerId].outcomes.push({
                  matchId,
                  betId,
                  id: outcomeId,
                  marketId,
                  specialBetValue,
                });
              }
            }
          }

          return acc;
        }, {});

        const groupIds = Object.keys(players);
        const groupArr = groupIds.map((groupId) => players[groupId]);
        const sorted = groupArr.sort((a, b) => a.order - b.order);

        const items = sorted
          .filter((item) => Object.keys(item.outcomes).length > 0)
          .map(({ label, outcomes, team }) => {
            let subLabel = null;
            if (team === 1) {
              subLabel = `(${match.competitor1Name})`;
            }
            if (team === 2) {
              subLabel = `(${match.competitor2Name})`;
            }
            return {
              label,
              subLabel,
              outcomes,
            };
          });

        return [
          {
            items,
            showPercentageDistribution: false,
            nbLines: items.length,
            expandPreviewLines: getExpandPreviewLines(items.length, betTemplate),
          },
        ];
      }

      return null;
    },
  );

  const selectGroupScorersPeriod: Selector<BetGroups | null> = createShallowEqualSelector(
    [
      selectSpecificBets,
      selectEntitiesPlayers,
      selectBetOutcomesWithValue,
      selectFirstBet,
      selectEntitiesMatchesEntity,
      selectFirstBetTemplate,
      selectEntitiesMatchesEntityAvailable,
      selectBetIsAvailable,
      selectSpecialBetGroup,
      getMatchIdFromProps,
      selectBetTemplate,
      selectPlayerIdFromFilterIdFromPropsOfPlayerKind,
    ],
    (
      bets,
      playersEntity,
      betOutcomesWithValue,
      firstBet,
      match,
      template,
      matchIsAvailable,
      betIsAvailable,
      specialBetGroup,
      matchId,
      betTemplate,
      playerIdFromFilterId,
    ) => {
      const matchingTemplates = [CustomBetTemplates.SOCCER_SCORERS_PERIOD, CustomBetTemplates.SOCCER_PLAYER_PROPS];
      const isMatching = !!~matchingTemplates.indexOf(betTemplate);

      if (isMatching) {
        let showPercentDistribution = false;

        const players = bets.reduce((acc, bet) => {
          const { outcomes: outcomeIds, betId, marketId, specialBetValue } = bet;
          const outcomesWithValue = betOutcomesWithValue[betId];

          for (const outcomeId of outcomeIds) {
            const outcome = outcomesWithValue.find((o) => o.outcomeId === outcomeId) ?? {};
            const { playerId, value, label, available, team, flag } = outcome;
            const player = playersEntity[playerId];

            // at least one outcome has a percent distribution
            if (!showPercentDistribution) {
              showPercentDistribution = !!outcome.percentDistribution;
            }

            if (playerId && (!playerIdFromFilterId || (playerIdFromFilterId && playerId === playerIdFromFilterId))) {
              if (!acc[playerId]) {
                acc[playerId] = {
                  label: player?.name ?? label,
                  team,
                  starter: player?.starter,
                  outcomes: [],
                  value,
                  flag,
                };
              }
              const mode = OddUtils.getMode({
                matchIsAvailable,
                betIsAvailable,
                template,
                oddValue: value,
                outcomeIsAvailable: available,
              });

              if (mode !== OutcomeModes.STOP) {
                acc[playerId].outcomes.push({
                  matchId,
                  betId,
                  id: outcomeId,
                  order: 100000 - value,
                  marketId,
                  specialBetValue,
                });
              }
            }
          }

          return acc;
        }, {});

        const playerIds = Object.keys(players);
        const playerArr = playerIds.map((playerId) => players[playerId]);
        const finalPlayers = playerArr
          .filter((item) => Object.keys(item.outcomes).length > 0)
          .map(({ label, team, starter, outcomes, flag }) => {
            return {
              label,
              team,
              starter,
              outcomes,
              flag,
            };
          });

        // Should order players by outcome ascending

        const sorted = finalPlayers.sort((a, b) => {
          const outcomeA = a.outcomes[0];
          const outcomeB = b.outcomes[0];

          if (outcomeA && outcomeB) {
            return outcomeB.order - outcomeA.order;
          }
          return 0;
        });

        const groups = sorted.reduce((acc, item) => {
          if (!acc[item.team]) {
            acc[item.team] = {
              items: [],
              label: MatchEntity.getCompetitorName(match, item.team),
              teamId: item.team,
              flag: item.flag,
            };
          }
          acc[item.team].items.push(item);
          return acc;
        }, {});

        const groupsIds = Object.keys(groups);
        const groupsArr = groupsIds.map((groupId) => groups[groupId]);

        return groupsArr.map(({ items, label, teamId, flag }) => ({
          groupLabel: label,
          teamId,
          flag: flag || MatchEntity.getCompetitorFlag(match, teamId),
          items: items,
          showPercentDistribution,
          nbLines: items?.length || 0,
          expandPreviewLines: getExpandPreviewLines(items.length, betTemplate),
        }));
      }

      return null;
    },
  );

  const selectGroupScorersLines: Selector<BetGroups | null> = createShallowEqualSelector(
    [
      selectSpecificBets,
      selectBetOutcomesWithValue,
      selectFirstBet,
      selectEntitiesMatchesEntity,
      selectFirstBetTemplate,
      selectEntitiesMatchesEntityAvailable,
      selectBetIsAvailable,
      selectSpecialBetGroup,
      getMatchIdFromProps,
      selectBetTemplate,
      selectEntitiesPlayers,
      selectPlayerIdFromFilterIdFromPropsOfPlayerKind,
    ],
    (
      bets,
      betOutcomesWithValue,
      firstBet,
      match,
      template,
      matchIsAvailable,
      betIsAvailable,
      specialBetGroup,
      matchId,
      betTemplate,
      playersEntity,
      playerIdFromFilterId,
    ) => {
      const matchingTemplates = [
        CustomBetTemplates.SOCCER_SCORERS_AND_TEAM_WINS,
        CustomBetTemplates.SOCCER_SCORERS_OVERTIME,
        CustomBetTemplates.MULTICHOICE_MM_SOCCER_SCORERS,
        CustomBetTemplates.MULTICHOICE_MM_ICE_HOCKEY_SCORERS,
        CustomBetTemplates.MULTICHOICE_MM_FOOTBALL_SCORERS,
        CustomBetTemplates.MULTICHOICE_MM_RUGBY_SCORERS,
        CustomBetTemplates.SOCCER_CARD_PLAYER,
      ];

      const isMatching = matchingTemplates.indexOf(betTemplate) !== -1;

      if (isMatching) {
        let showPercentDistribution = false;
        const disableDivider = !!~[CustomBetTemplates.SOCCER_CARD_PLAYER].indexOf(betTemplate);

        const players = bets.reduce((acc, bet) => {
          const { outcomes: outcomeIds, betId, marketId, specialBetValue } = bet;
          const outcomesWithValue = betOutcomesWithValue[betId];

          for (const outcomeId of outcomeIds) {
            const outcome = outcomesWithValue.find((o) => o.outcomeId === outcomeId) ?? {};
            const { playerId, value, label, available, team, flag } = outcome;

            // at least one outcome has a percent distribution
            if (!showPercentDistribution) {
              showPercentDistribution = !!outcome.percentDistribution;
            }

            if (playerId && (!playerIdFromFilterId || (playerIdFromFilterId && playerId === playerIdFromFilterId))) {
              if (!acc[playerId]) {
                acc[playerId] = {
                  label,
                  starter: playersEntity[playerId]?.starter,
                  team,
                  value,
                  flag,
                };
              }
              const mode = OddUtils.getMode({
                matchIsAvailable,
                betIsAvailable,
                template,
                oddValue: value,
                outcomeIsAvailable: available,
              });

              if (mode !== OutcomeModes.STOP) {
                acc[playerId].outcome = {
                  ...acc[playerId],
                  matchId,
                  betId,
                  id: outcomeId,
                  order: 100000 - (value ?? 0),
                  marketId,
                  specialBetValue,
                };
              }
            }
          }

          return acc;
        }, {});

        const playerIds = Object.keys(players);
        const playerArr = playerIds.map((playerId) => players[playerId]);
        const sorted = playerArr.sort((a, b) => {
          const orderA = a?.outcome?.order ?? 0;
          const orderB = b?.outcome?.order ?? 0;

          if (orderA - orderB !== 0) {
            return -Math.sign(orderA - orderB);
          }
          return 0;
        });

        const groups = sorted
          .filter((item) => !!item.outcome)
          .reduce((acc, item) => {
            if (!acc[item.team]) {
              acc[item.team] = {
                items: [],
                label: MatchEntity.getCompetitorName(match, item.team),
                teamId: item.team,
                flag: item.flag,
              };
            }
            acc[item.team].items.push({
              ...item.outcome,
              disableDivider,
            });
            return acc;
          }, {});

        const groupsIds = Object.keys(groups);
        const groupsArr = groupsIds.map((groupId) => groups[groupId]);

        return groupsArr.map(({ items, label, teamId, flag }) => ({
          groupLabel: label,
          flag: flag || MatchEntity.getCompetitorFlag(match, teamId),
          teamId,
          items: items,
          showPercentDistribution,
          nbLines: items?.length ?? 0,
          expandPreviewLines: getExpandPreviewLines(items.length, betTemplate),
        }));
      }

      return null;
    },
  );

  const selectGroupColumnsProperties: Selector<BetGroupEntity> = createSelector(
    [
      selectBetOutcomesWithValue,
      selectBetTemplate,
      selectBetTypeId,
      getMatchIdFromProps,
      getBetsDataFromProps,
      selectPlayerIdFromFilterIdFromPropsOfPlayerKind,
      selectEntitiesBets,
      selectEntitiesOutcomes,
      selectEntitiesPlayers,
    ],
    (
      betOutcomesWithValue,
      template,
      betType,
      matchId,
      betsData,
      playerIdFromFilterId,
      betsMap,
      outcomesMap,
      playersMap,
    ) => {
      return (
        betsData?.reduce(
          (properties, betData) => {
            const { betId } = betData ?? {};
            const curr = {
              items: [...properties.items],
              outcomes: [...properties.outcomes],
              nbLines: properties.nbLines,
              expandPreviewLines: properties.expandPreviewLines,
            };
            const { marketId, specialBetValue } = betsMap[betId] ?? {};
            const betOutcomes = betOutcomesWithValue[betId];
            const orderedBetOutcomes =
              template && betOutcomes ? getOrderedOutcomes(betOutcomes, template) : betOutcomes;
            curr.outcomes.push(...(orderedBetOutcomes ?? []));

            if ([BetTemplates.SET_SCORE, BetTemplates.SCORE].indexOf(template) !== -1) {
              const tmpItems = getColumnItems(betType, orderedBetOutcomes, matchId, betId, marketId, specialBetValue);
              curr.items.push(...(tmpItems ?? []));
              curr.nbLines += tmpItems.reduce((acc, cur) => Math.max(acc, cur.length), 0);
            } else {
              const tmpItems = formatBetGroupOutcomes(orderedBetOutcomes, matchId, betId, marketId, specialBetValue);
              curr.items.push(...(tmpItems ?? []));
              if (template) {
                const nbColumns = getNbColumns(template, tmpItems.length);
                curr.nbLines += Math.ceil(tmpItems.length / nbColumns);
              } else {
                curr.nbLines += tmpItems.length;
              }
            }
            curr.expandPreviewLines = getExpandPreviewLines(curr.nbLines, template);

            if (playerIdFromFilterId) {
              const { srPlayerId: srPlayerIdFromFilterId } = playersMap[playerIdFromFilterId] ?? {};

              curr.items = curr.items.reduce((acc, item) => {
                const { id: outcomeId } = item;
                const outcome = outcomesMap[outcomeId];

                if (outcome) {
                  const { srPlayerId, code } = outcome;
                  const srPlayerIdsFromCode = getSrPlayerIdsFromSBV(code);

                  if (
                    !srPlayerIdFromFilterId ||
                    (srPlayerIdFromFilterId && srPlayerId === srPlayerIdFromFilterId) ||
                    (Array.isArray(srPlayerIdsFromCode) && !!~srPlayerIdsFromCode.indexOf(srPlayerIdFromFilterId))
                  ) {
                    return [...acc, item];
                  }
                }

                return acc;
              }, []);
            }

            return curr;
          },
          {
            items: [],
            outcomes: [],
            nbLines: 0,
            expandPreviewLines: 0,
          },
        ) ?? emptyObject
      );
    },
  );

  const selectGroupColumns: Selector<BetGroups> = createShallowEqualSelector(
    [selectGroupColumnsProperties, selectFirstBetTemplate],
    (columnProperties, firstBetTemplate) => {
      const { items, outcomes, nbLines } = columnProperties;
      // at least one outcome has a percent distribution
      const showPercentDistribution = some(outcomes, (outcome) => !!outcome?.percentDistribution);
      return [
        {
          items,
          showPercentDistribution,
          nbLines,
          expandPreviewLines: getExpandPreviewLines(nbLines, firstBetTemplate),
        },
      ];
    },
  );

  const selectGroupMultipleScorers: Selector<BetGroups | undefined> = createShallowEqualSelector(
    [
      selectSpecificBets,
      selectBetOutcomesWithValue,
      selectFirstBet,
      selectEntitiesMatchesEntity,
      selectFirstBetTemplate,
      selectEntitiesMatchesEntityAvailable,
      selectBetIsAvailable,
      selectSpecialBetGroup,
      getMatchIdFromProps,
      selectBetTemplate,
      selectEntitiesPlayers,
      selectPlayerIdFromFilterIdFromPropsOfPlayerKind,
    ],
    (
      bets,
      betOutcomesWithValue,
      firstBet,
      match,
      template,
      matchIsAvailable,
      betIsAvailable,
      specialBetGroup,
      matchId,
      betTemplate,
      playersMap,
      playerIdFromFilterId,
    ) => {
      const matchingTemplates = [
        CustomBetTemplates.ICE_HOCKEY_MULTIPLE_SCORERS,
        CustomBetTemplates.RUGBY_MULTIPLE_SCORERS,
      ];

      const isMatching = matchingTemplates.indexOf(betTemplate) !== -1;
      const playerFromFilterId = playersMap[playerIdFromFilterId];
      const { srPlayerId: srPlayerIdFromFilterId } = playerFromFilterId ?? {};

      if (isMatching) {
        const players = bets.reduce((acc, bet, index) => {
          const { outcomes: outcomeIds, betId, betTitle, marketId, specialBetValue } = bet;
          const outcomesWithValue = betOutcomesWithValue[betId];
          const specifier = specialBetValue?.split('|')?.[0];

          for (const outcomeId of outcomeIds) {
            const outcome = outcomesWithValue.find((o) => o.outcomeId === outcomeId) ?? {};
            const { value, available, team, srPlayerId, code } = outcome;
            const srPlayerIdsFromCode = getSrPlayerIdsFromSBV(code);

            if (
              specifier &&
              (!srPlayerIdFromFilterId ||
                (srPlayerIdFromFilterId && srPlayerId === srPlayerIdFromFilterId) ||
                (Array.isArray(srPlayerIdsFromCode) && !!~srPlayerIdsFromCode.indexOf(srPlayerIdFromFilterId)))
            ) {
              if (!acc[specifier]) {
                acc[specifier] = {
                  label: betTitle?.split(' - ')?.[1],
                  outcomes: [],
                  team,
                  srPlayerId,
                  order: index,
                  marketId,
                  specialBetValue,
                };
              }
              const mode = OddUtils.getMode({
                matchIsAvailable,
                betIsAvailable,
                template,
                oddValue: value,
                outcomeIsAvailable: available,
              });

              if (mode !== OutcomeModes.STOP) {
                acc[specifier].outcomes.push({
                  matchId,
                  betId,
                  id: outcomeId,
                  marketId,
                  specialBetValue,
                });
              }
            }
          }

          return acc;
        }, {});

        const groupIds = Object.keys(players);
        const groupArr = groupIds.map((groupId) => players[groupId]);
        const sorted = groupArr.sort((a, b) => a.order - b.order);

        const items = sorted
          .filter((item) => Object.keys(item.outcomes).length > 0)
          .map(({ label, outcomes, team }) => {
            let subLabel = null;
            if (team === 1) {
              subLabel = `(${match.competitor1Name})`;
            }
            if (team === 2) {
              subLabel = `(${match.competitor2Name})`;
            }
            return {
              label,
              subLabel,
              outcomes,
            };
          });

        return [
          {
            items,
            showPercentDistribution: false,
            nbLines: items.length,
            expandPreviewLines: getExpandPreviewLines(items.length, betTemplate),
          },
        ];
      }
    },
  );

  const selectGroupTeamsScorers: Selector<BetGroups | undefined> = createShallowEqualSelector(
    [
      selectSpecificBets,
      selectBetOutcomesWithValue,
      selectFirstBet,
      selectEntitiesMatchesEntity,
      selectFirstBetTemplate,
      selectEntitiesMatchesEntityAvailable,
      selectBetIsAvailable,
      selectSpecialBetGroup,
      getMatchIdFromProps,
      selectBetTemplate,
      selectEntitiesPlayers,
      selectPlayerIdFromFilterIdFromPropsOfPlayerKind,
    ],
    (
      bets,
      betOutcomesWithValue,
      firstBet,
      match,
      template,
      matchIsAvailable,
      betIsAvailable,
      specialBetGroup,
      matchId,
      betTemplate,
      playersMap,
      playerIdFromFilterId,
    ) => {
      const matchingTemplates = [
        CustomBetTemplates.BASKETBALL_POINT_PLAYER,
        CustomBetTemplates.BASKETBALL_PLAYER_OVER_UNDER,
        CustomBetTemplates.SOCCER_MULTIPLE_SCORERS,
      ];

      const isMatching = matchingTemplates.indexOf(betTemplate) !== -1;
      const playerFromFilterId = playersMap[playerIdFromFilterId];
      const { srPlayerId: srPlayerIdFromFilterId } = playerFromFilterId ?? {};

      if (isMatching) {
        const players = bets.reduce((acc, bet, index) => {
          const { outcomes: outcomeIds, betId, betTitle, betGroup, marketId, specialBetValue } = bet;
          const outcomesWithValue = betOutcomesWithValue[betId];

          for (const outcomeId of outcomeIds) {
            const outcome = outcomesWithValue.find((o) => o.outcomeId === outcomeId) ?? {};
            const { value, available, team, srPlayerId, code } = outcome;
            const srPlayerIdsFromCode = getSrPlayerIdsFromSBV(code);

            if (
              betGroup &&
              (!srPlayerIdFromFilterId ||
                (srPlayerIdFromFilterId && srPlayerId === srPlayerIdFromFilterId) ||
                (Array.isArray(srPlayerIdsFromCode) && !!~srPlayerIdsFromCode.indexOf(srPlayerIdFromFilterId)))
            ) {
              if (!acc[betGroup]) {
                acc[betGroup] = {
                  label: betTitle?.split(' - ')?.[1],
                  outcomes: [],
                  team,
                  srPlayerId,
                  order: index,
                  marketId,
                  specialBetValue,
                };
              }
              const mode = OddUtils.getMode({
                matchIsAvailable,
                betIsAvailable,
                template,
                oddValue: value,
                outcomeIsAvailable: available,
              });

              if (mode !== OutcomeModes.STOP) {
                acc[betGroup].outcomes.push({
                  matchId,
                  betId,
                  id: outcomeId,
                  marketId,
                  specialBetValue,
                });
              }
            }
          }

          return acc;
        }, {});

        const groupIds = Object.keys(players);
        const groupArr = groupIds.map((groupId) => players[groupId]);
        const sorted = groupArr.sort((a, b) => a.order - b.order);

        const items = sorted
          .filter((item) => Object.keys(item.outcomes).length > 0)
          .map(({ label, outcomes, team }) => {
            let subLabel = null;
            if (team === 1) {
              subLabel = `(${match.competitor1Name})`;
            }
            if (team === 2) {
              subLabel = `(${match.competitor2Name})`;
            }
            return {
              label,
              // Si une team est définie, on affiche le flag associé pour le joueur
              // (permet de ne pas afficher le flag lorsqu'il y a plusieurs joueurs)
              playerFlag: team ? MatchEntity.getCompetitorFlag(match, team) : null,
              subLabel,
              outcomes,
            };
          });

        return [
          {
            items,
            showPercentDistribution: false,
            nbLines: items.length,
            expandPreviewLines: getExpandPreviewLines(items.length, betTemplate),
          },
        ];
      }
    },
  );

  const selectGroupInfos: Selector<BetGroups> = createShallowEqualSelector(
    [
      selectGroupOverUnder,
      selectGroupAsians,
      selectGroupScorerGoalType,
      selectGroupCompetitorGoalType,
      selectGroupScorers,
      selectGroupScorersLines,
      selectGroupScorersPeriod,
      selectGroupDecisivePlayers,
      selectGroupTeamsScorers,
      selectGroupMultipleScorers,
      selectScorerHandball,
      selectGroupColumns,
      selectGroupHeadToHead,
    ],
    (
      groupOverUnder,
      groupAsians,
      groupGoalType,
      groupCompetitorGoalType,
      groupScorers,
      groupScorersLines,
      groupScorersPeriod,
      groupDecisivePlayers,
      groupTeamsScorers,
      groupMultipleScorers,
      groupScorerHandball,
      groupColumns,
      groupHeadToHead,
    ) => {
      const groups =
        groupHeadToHead ||
        groupOverUnder ||
        groupAsians ||
        groupGoalType ||
        groupCompetitorGoalType ||
        groupScorers ||
        groupScorersLines ||
        groupScorersPeriod ||
        groupDecisivePlayers ||
        groupTeamsScorers ||
        groupMultipleScorers ||
        groupScorerHandball ||
        groupColumns;

      // We don't want to have any group without any items (happens in certain filtering cases)
      return groups.filter((group) => !!group?.items?.length);
    },
  );

  const selectShowPercentDistributionFromGroupInfos: Selector<boolean> = createSelector(
    [selectGroupInfos],
    (groupInfos) => (groupInfos ? groupInfos[0]?.showPercentDistribution ?? false : false),
  );

  const selectTournamentFromMatch: Selector<TournamentState | undefined> = createSelector(
    [selectEntitiesMatchesEntity, selectEntitiesTournaments],
    (match, tournaments) => tournaments[match?.tournamentId],
  );

  const selectEnablePlayerStats: Selector<boolean> = createSelector(
    [selectEntitiesMatchesEntity, selectTournamentFromMatch],
    (match, tournament) => MatchEntity.hasPlayerStatsTracker(match, tournament),
  );

  const selectItemsMappingFunction: Selector<(item: BetGroupPlayer) => BetGroupPlayer> = createSelector(
    [selectEntitiesOutcomes, selectEntitiesBets, getMatchIdFromProps],
    (outcomes, bets, matchId) => (item: BetGroupPlayer) => {
      const outcomesFromItem = item?.outcomes;

      if (outcomesFromItem) {
        const firstOutcomeId = outcomesFromItem[0]?.id;
        if (firstOutcomeId) {
          const outcome = outcomes[firstOutcomeId];

          if (outcome) {
            return {
              ...item,
              playerTrackerInfos: {
                playerSrId: OutcomeEntity.getPlayerId(outcome),
                playerTeam: outcome.team,
                matchId,
              },
            };
          }
          return item;
        }
      }

      if (item.id) {
        const outcome = outcomes[item.id];
        const bet = bets[item.betId];

        if (outcome) {
          return {
            ...item,
            playerTrackerInfos: {
              playerSrId: OutcomeEntity.getPlayerId(outcome, bet?.specialBetValue),
              playerTeam: outcome.team,
              matchId: matchId,
            },
          };
        }
        return item;
      }
      return item;
    },
  );

  const selectEnhancedGroups: Selector<BetGroups> = createShallowEqualSelector(
    [selectGroupInfos, selectItemsMappingFunction],
    (groups, $itemsMappingFunction) =>
      groups.map((group) => ({
        ...group,
        items: group.items?.map($itemsMappingFunction) ?? [],
      })),
  );

  const selectGroups: Selector<BetGroups> = createSelector(
    [selectFirstBet, selectSpecialBetGroup, selectEnablePlayerStats, selectGroupInfos, selectEnhancedGroups],
    (firstBet, specialBetGroup, enablePlayerStats, groups, enhancedGroups) => {
      if (enablePlayerStats && firstBet) {
        const allowPlayerStatsTracker = BetEntity.allowPlayerStatsTracker(firstBet);
        if (
          specialBetGroup === SpecialBetGroups.FOOTBALL_SCORERS ||
          specialBetGroup === SpecialBetGroups.MULTICHOICE_MM_FOOTBALL_SCORERS ||
          specialBetGroup === SpecialBetGroups.RUGBY_7S_SCORERS ||
          specialBetGroup === SpecialBetGroups.RUGBY_7S_SCORERS_LIVE ||
          specialBetGroup === SpecialBetGroups.RUGBY_LEAGUE_SCORERS ||
          specialBetGroup === SpecialBetGroups.RUGBY_LEAGUE_SCORERS_LIVE ||
          specialBetGroup === SpecialBetGroups.RUGBY_SCORERS ||
          specialBetGroup === SpecialBetGroups.RUGBY_SCORERS_LIVE ||
          specialBetGroup === SpecialBetGroups.MULTICHOICE_MM_RUGBY_SCORERS ||
          specialBetGroup === SpecialBetGroups.MULTICHOICE_MM_RUGBY_LEAGUE_SCORERS ||
          specialBetGroup === SpecialBetGroups.ICE_HOCKEY_SCORERS ||
          specialBetGroup === SpecialBetGroups.MULTICHOICE_MM_ICE_HOCKEY_SCORERS ||
          specialBetGroup === SpecialBetGroups.ICE_HOCKEY_SCORER_PERIOD ||
          specialBetGroup === SpecialBetGroups.ICE_HOCKEY_SCORER_LIVE ||
          specialBetGroup === SpecialBetGroups.ICE_HOCKEY_MULTI_SCORER_LIVE ||
          specialBetGroup === SpecialBetGroups.SOCCER_SCORERS_PREMATCH ||
          specialBetGroup === SpecialBetGroups.SOCCER_SCORERS_LIVE ||
          specialBetGroup === SpecialBetGroups.SOCCER_DECISIVE_PLAYERS ||
          specialBetGroup === SpecialBetGroups.MULTICHOICE_MM_SOCCER_SCORERS ||
          specialBetGroup === SpecialBetGroups.SOCCER_SCORERS_LIVE_WITHOUT_LAST_SCORER ||
          specialBetGroup === SpecialBetGroups.SOCCER_MULTI_SCORERS_PREMATCH ||
          specialBetGroup === SpecialBetGroups.SOCCER_MULTI_SCORERS_LIVE ||
          specialBetGroup === SpecialBetGroups.SOCCER_SCORER_PERIOD_10MINUTES ||
          specialBetGroup === SpecialBetGroups.SOCCER_SCORER_PERIOD_15MINUTES ||
          specialBetGroup === SpecialBetGroups.SOCCER_SCORER_PERIOD_HALF_TIME ||
          specialBetGroup === SpecialBetGroups.SOCCER_SCORERS_GOAL_TYPE ||
          specialBetGroup === SpecialBetGroups.SOCCER_MULTI_SCORER_1_HT ||
          specialBetGroup === SpecialBetGroups.SOCCER_MULTI_SCORER_2_HT ||
          specialBetGroup === SpecialBetGroups.SOCCER_PLAYER_PROPS_ASSISTS ||
          specialBetGroup === SpecialBetGroups.SOCCER_PLAYER_PROPS_SHOTS ||
          specialBetGroup === SpecialBetGroups.SOCCER_PLAYER_PROPS_SHOTS_ON_GOAL ||
          specialBetGroup === SpecialBetGroups.SOCCER_PLAYER_PROPS_PASSES ||
          specialBetGroup === SpecialBetGroups.SOCCER_PLAYER_PROPS_TACKLES ||
          specialBetGroup === SpecialBetGroups.BASKETBALL_POINT_PLAYER ||
          specialBetGroup === SpecialBetGroups.BASKETBALL_PASS_PLAYER ||
          specialBetGroup === SpecialBetGroups.BASKETBALL_REBOUND_PLAYER ||
          specialBetGroup === SpecialBetGroups.BASKETBALL_POINT_PLAYER_OVER_UNDER ||
          specialBetGroup === SpecialBetGroups.BASKETBALL_PASS_PLAYER_OVER_UNDER ||
          specialBetGroup === SpecialBetGroups.BASKETBALL_REBOUND_PLAYER_OVER_UNDER ||
          specialBetGroup === SpecialBetGroups.BASKETBALL_3POINTS_PLAYER_OVER_UNDER ||
          specialBetGroup === SpecialBetGroups.BASKETBALL_POINT_REBOUND_ASSIST_PLAYER_OVER_UNDER ||
          specialBetGroup === SpecialBetGroups.BASKETBALL_POINT_PASS_PLAYER_OVER_UNDER ||
          specialBetGroup === SpecialBetGroups.BASKETBALL_PASS_REBOUND_PLAYER_OVER_UNDER ||
          specialBetGroup === SpecialBetGroups.BASKETBALL_POINT_REBOUND_PLAYER_OVER_UNDER ||
          specialBetGroup === SpecialBetGroups.BASKETBALL_PLAYER_INTERCEPT_OVER_UNDER ||
          specialBetGroup === SpecialBetGroups.BASKETBALL_PLAYER_BLOCK_OVER_UNDER ||
          specialBetGroup === SpecialBetGroups.BASKETBALL_PLAYER_LOST_BALL_OVER_UNDER ||
          specialBetGroup === SpecialBetGroups.BASKETBALL_TOTAL_PLAYER_INTERCEPT_BLOCK_OVER_UNDER ||
          specialBetGroup === SpecialBetGroups.BASKETBALL_TOTAL_PLAYER_POINTS_REBOUND_ASSIST ||
          specialBetGroup === SpecialBetGroups.BASKETBALL_TOTAL_PLAYER_POINTS_ASSIST ||
          specialBetGroup === SpecialBetGroups.BASKETBALL_TOTAL_PLAYER_ASSIST_REBOUND ||
          specialBetGroup === SpecialBetGroups.BASKETBALL_TOTAL_PLAYER_POINTS_REBOUND ||
          specialBetGroup === SpecialBetGroups.BASKETBALL_PLAYER_INTERCEPT ||
          specialBetGroup === SpecialBetGroups.BASKETBALL_PLAYER_BLOCK ||
          specialBetGroup === SpecialBetGroups.BASKETBALL_PLAYER_LOST_BALL ||
          specialBetGroup === SpecialBetGroups.BASKETBALL_TOTAL_PLAYER_INTERCEPT_BLOCK ||
          specialBetGroup === SpecialBetGroups.HANDBALL_SCORERS_PLAYER ||
          allowPlayerStatsTracker
        ) {
          return enhancedGroups;
        }
      }

      return groups;
    },
  );
  const selectSanitizedGroups: Selector<BetGroups> = createSelector(
    [selectEntitiesMatchesEntitySportId, getMatchIdFromProps, selectFirstBetId, selectGroups],
    (sportId, matchId, firstBetId, groups) => {
      if (sportId) {
        const defaultTemplate = MainBetOutcomesBySport[sportId]
          ? MainBetOutcomesBySport[sportId]
          : DefaultMainBetOutcomes;

        const fallBackItems = defaultTemplate.map<{
          matchId?: number | null;
          betId?: number | null;
          id?: number | null;
          marketId?: number | null;
          specialBetValue?: string | null;
        }>(() => ({
          matchId,
          betId: null,
          id: null,
          marketId: null,
          specialBetValue: null,
        }));

        const sanitizedGroups = firstBetId ? groups : [{ items: fallBackItems, nbLines: 1, previewLines: 1 }];

        return sanitizedGroups.map((group) => ({
          ...group,
        }));
      }
      return emptyArray;
    },
  );

  const selectTemplateHeaders: Selector<Array<string> | null> = createSelector(
    [selectSpecialBetGroup],
    (specialBetGroup) => getTemplateHeaders(specialBetGroup),
  );

  const selectFirstBetOutcomes: Selector<Array<number>> = createSelector(
    [selectFirstBet],
    (firstBet) => firstBet?.outcomes,
  );

  const selectFirstBetActualOutcomes: Selector<Array<OutcomeState>> = createSelector(
    [selectFirstBetOutcomes, selectEntitiesOutcomes],
    (firstBetOutcomes, outcomes) => firstBetOutcomes?.map((outcomeId) => outcomes[outcomeId]) ?? emptyArray,
  );

  const selectGroupFactoryHeaders: Selector<Array<string>> = createSelector(
    [selectFirstBet, selectFirstBetTemplate, selectTemplateHeaders, selectFirstBetActualOutcomes],
    (firstBet, firstBetTemplate, templateHeaders, betOutcomes) => {
      if (firstBet) {
        let headers = templateHeaders;
        if (firstBetTemplate === BetTemplates.ASIAN_HANDICAP) {
          headers = betOutcomes.map<string>((outcome) => {
            const splited = (outcome?.label || '').split(' ');
            return splited.splice(0, splited.length - 1).join(' ');
          });
        }
        return headers;
      }
      return emptyArray;
    },
  );

  const selectShowLineUpButton: Selector<boolean> = createSelector([selectSanitizedGroups], (groups) =>
    groups.every((group) => group?.teamId && group?.items?.some((item) => item.starter)),
  );

  const selectShowStarterFilter: Selector<boolean> = createSelector(
    [selectEntitiesMatchesEntityStatus, selectSanitizedGroups, selectBetTemplate],
    (matchStatus, groups, betTemplate) => {
      if (
        (typeof matchStatus === 'string' && matchStatus.toLowerCase() !== MatchStatuses.PREMATCH) ||
        !~[
          CustomBetTemplates.SOCCER_SCORERS,
          CustomBetTemplates.SOCCER_GOAL_TYPE,
          CustomBetTemplates.SOCCER_MULTIPLE_SCORERS,
          CustomBetTemplates.ICE_HOCKEY_MULTIPLE_SCORERS,
          CustomBetTemplates.SOCCER_SCORERS_AND_TEAM_WINS,
          CustomBetTemplates.SOCCER_SCORERS_PERIOD,
          CustomBetTemplates.SOCCER_PLAYER_PROPS,
          CustomBetTemplates.MULTICHOICE_MM_SOCCER_SCORERS,
          CustomBetTemplates.MULTICHOICE_MM_RUGBY_SCORERS,
          CustomBetTemplates.MULTICHOICE_MM_ICE_HOCKEY_SCORERS,
        ].indexOf(betTemplate)
      ) {
        return false;
      }

      return groups.every((group) => group?.items?.some((item) => item.starter));
    },
  );

  const selectAvailablePresentationModes: Selector<Array<PresentationItem>> = createSelector(
    [selectSanitizedGroups, selectBetTemplate, selectMarketId, selectEntitiesMatchesEntity],
    (groups, betTemplate, marketId, match) => {
      const modes = [{ type: PresentationModes.CLASSIC }];
      if (MatchEntity.isEnded(match)) {
        return modes;
      }

      if (marketWithoutSlider.includes(marketId)) {
        return modes;
      }

      if ([BetTemplates.ASIAN_HANDICAP, BetTemplates.OVER_UNDER].includes(betTemplate)) {
        const entry: PresentationItem = { type: PresentationModes.SLIDER_BY_BET };
        const nbBets = groups?.reduce((acc, group) => acc + (group?.nbLines ?? 0), 0);
        // On vérifie qu'on a bien au 5 bets présent
        let shouldAddMode = nbBets >= MINIMUM_BETS_FOR_BET_SLIDER_PRESENTATION;

        if (betTemplate === BetTemplates.ASIAN_HANDICAP) {
          entry.labelSpecifier = 'hcp';
          entry.defaultPosition = PresentationDefaultPositions.MIDDLE;

          if (haveOutcomeWithoutSpecifier(groups, 'hcp')) {
            shouldAddMode = false;
          }
        } else if (betTemplate === BetTemplates.OVER_UNDER) {
          entry.labelSpecifier = 'total';
          entry.defaultPosition = PresentationDefaultPositions.START;

          if (haveOutcomeWithoutSpecifier(groups, 'total')) {
            shouldAddMode = false;
          }
        }

        if (shouldAddMode) {
          modes.push(entry);
        }
      } else if (
        [CustomBetTemplates.BASKETBALL_PLAYER_OVER_UNDER, CustomBetTemplates.BASKETBALL_POINT_PLAYER].includes(
          betTemplate,
        )
      ) {
        const hasOutcomesWithoutTotalSpecifier = haveOutcomeWithoutSpecifier(groups, 'total');

        if (havePlayerAtLeast3Lines(groups) && !hasOutcomesWithoutTotalSpecifier) {
          modes.push({
            type: PresentationModes.SLIDER_BY_OUTCOME,
            labelSpecifier: 'total',
            defaultPosition: PresentationDefaultPositions.START,
          });
        }
      } else if ([CustomBetTemplates.SOCCER_PLAYER_PROPS].includes(betTemplate)) {
        if (havePlayerAtLeast3Lines(groups)) {
          modes.push({
            type: PresentationModes.SLIDER_BY_OUTCOME,
            defaultPosition: PresentationDefaultPositions.START,
          });
        }
      } else if ([CustomBetTemplates.HANDBALL_SCORERS_PLAYER].includes(betTemplate)) {
        const hasOutcomesWithoutGoalSpecifier = haveOutcomeWithoutSpecifier(groups, 'goals');

        if (havePlayerAtLeast3Lines(groups) && !hasOutcomesWithoutGoalSpecifier) {
          modes.push({
            type: PresentationModes.SLIDER_BY_OUTCOME,
            labelSpecifier: 'goals',
            defaultPosition: PresentationDefaultPositions.START,
          });
        }
      } else if (
        [
          MarketIds.MARKET_ID_PLAYER_ACES,
          MarketIds.MARKET_ID_PLAYER_ACES_COMPETITOR1,
          MarketIds.MARKET_ID_PLAYER_ACES_COMPETITOR2,
          MarketIds.MARKET_ID_SOCCER_EXACT_GOALS,
          MarketIds.MARKET_ID_SOCCER_TEAM_OVER_GOALS,
          MarketIds.MARKET_ID_SOCCER_HOME_GOALS,
          MarketIds.MARKET_ID_SOCCER_AWAY_GOALS,
          MarketIds.MARKET_ID_SOCCER_EXACT_GOALS_HT,
          MarketIds.MARKET_ID_SOCCER_TEAM_OVER_GOALS_HT,
          MarketIds.MARKET_ID_SOCCER_TOTAL_WINS_HOME,
          MarketIds.MARKET_ID_SOCCER_TOTAL_WINS_AWAY,
          MarketIds.MARKET_ID_SOCCER_EXACT_DRAW,
        ].includes(marketId)
      ) {
        const nbBets = groups?.reduce((acc, group) => acc + (group?.nbLines ?? 0), 0);
        if (nbBets >= MINIMUM_BETS_FOR_BET_SLIDER_PRESENTATION) {
          modes.push({
            type: PresentationModes.SLIDER_BY_OUTCOME,
            defaultPosition: PresentationDefaultPositions.START,
            disableExpand: true,
          });
        }
      }

      return modes;
    },
  );

  const selectShowNewTag = createSelector(
    [selectBetTemplate, selectBetTypeId, selectMarketId, selectRuntimeLicence],
    (betTemplate, betTypeId, marketId, licence) => {
      return (
        betTemplate === CustomBetTemplates.SOCCER_DECISIVE_PLAYERS ||
        !!~NewBetTypesByLicence[licence].indexOf(betTypeId) ||
        !!~NewMarketIdsByLicence[licence].indexOf(marketId)
      );
    },
  );

  return createSelector(
    [
      selectSanitizedGroups,
      selectGroupFactoryHeaders,
      selectBetTemplate,
      selectEntitiesMatchesEntityAvailable,
      selectShowPercentDistributionFromGroupInfos,
      selectShowLineUpButton,
      selectShowStarterFilter,
      selectAvailablePresentationModes,
      selectBetTypeId,
      selectMarketId,
      selectIsAlternativeMainBet,
      selectBetTitle,
      selectBetId,
      selectBetIsMultichoice,
      selectShowNewTag,
      selectIsMatchMultimatch,
      selectBetCurrentTotal,
    ],
    (
      groups,
      headers,
      betTemplate,
      matchIsAvailable,
      showPercentDistribution,
      showLineUpButton,
      showStarterFilter,
      betPresentationModes,
      betTypeId,
      marketId,
      isAlternativeMainBet,
      betTitle,
      betId,
      isMultichoice,
      showNewTag,
      isMultimatch,
      currentTotal,
    ) => {
      return {
        groups,
        headers,
        betTemplate,
        matchIsAvailable,
        showPercentDistribution,
        showLineUpButton,
        showStarterFilter,
        betPresentationModes,
        betTypeId,
        marketId,
        isAlternativeMainBet,
        betTitle,
        betId,
        isMultichoice,
        showNewTag,
        isMultimatch,
        currentTotal,
      };
    },
  );
};

export default makeBetGroupFactoryPropsSelector;
